import { useCallback, useState } from "react";
import { Play, RotateCcw } from "lucide-react";
import MinigameStage from "./MinigameStage";
import MinigameField from "./MinigameField";
import StreakerInteractiveModal from "../game/StreakerInteractiveModal";

// The stage design space — a square arena so the field fits with true
// proportions. All chase physics run in this fixed space, so the run feels
// identical at every screen size.
const STAGE_W = 600;
const STAGE_H = 600;
const EDGES = ["top", "bottom", "left", "right"];
const BEST_KEY = "junkball_streak_best_ms";

// Fresh randomized run parameters. The exit edge is forced to differ from the
// spawn edge so a run is never a trivial straight walk-back.
function _randomRun() {
  const spawn = EDGES[Math.floor(Math.random() * EDGES.length)];
  let exit = EDGES[Math.floor(Math.random() * EDGES.length)];
  if (exit === spawn) exit = EDGES[(EDGES.indexOf(spawn) + 1) % EDGES.length];
  return {
    seed: (Math.random() * 0xffffffff) >>> 0,
    spawn_edge: spawn,
    exit_edge: exit,
  };
}

function _fmt(ms) {
  return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
}

// Pre-game panel — the chase only starts when the player hits Play.
function StartPanel({ best, onPlay }) {
  return (
    <div className="absolute inset-0 z-[1200] flex flex-col items-center justify-center gap-3
                    bg-slate-950/75 backdrop-blur-sm text-center px-6">
      <div className="text-4xl">🏃</div>
      <h3 className="text-2xl font-black text-white">Streak Yourself</h3>
      <p className="text-xs text-slate-400 max-w-xs">
        Arrow keys to move · hold Space to sprint · reach the glowing exit before the guards tag you.
      </p>
      {best != null && (
        <div className="text-xs text-slate-500">
          Best escape: <span className="font-bold text-amber-400">{_fmt(best)}</span>
        </div>
      )}
      <button
        onClick={onPlay}
        className="mt-2 flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400
                   text-slate-950 font-black text-sm transition-colors"
      >
        <Play size={15} /> Play
      </button>
    </div>
  );
}

// Shown after a run resolves (the modal plays its own ESCAPED/CAUGHT FX first,
// then calls onClose — which swaps this panel in).
function ResultPanel({ result, best, onPlayAgain }) {
  const escaped = result?.outcome === "escaped";
  return (
    <div className="absolute inset-0 z-[1200] flex flex-col items-center justify-center gap-3
                    bg-slate-950/80 backdrop-blur-sm text-center px-6">
      <div className={`text-3xl font-black ${escaped ? "text-emerald-400" : "text-rose-400"}`}>
        {escaped ? "ESCAPED! 🎉" : "CAUGHT 👮"}
      </div>
      <div className="text-sm text-slate-300">
        You lasted <span className="font-black text-white">{_fmt(result?.elapsed_ms || 0)}</span>
      </div>
      <div className="text-xs text-slate-500">
        Best escape: <span className="font-bold text-amber-400">{best != null ? _fmt(best) : "—"}</span>
      </div>
      <button
        onClick={onPlayAgain}
        className="mt-2 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400
                   text-slate-950 font-black text-sm transition-colors"
      >
        <RotateCcw size={15} /> Play again
      </button>
    </div>
  );
}

/**
 * Streak Yourself — the standalone, client-side Junk Minigame. No backend game,
 * no websocket: it reuses the in-game `StreakerInteractiveModal` chase engine
 * in `embedded` mode, rendered inside a fixed 600×600 `MinigameStage`. The
 * chase does not auto-start — the player presses Play (then Play again).
 */
export default function StreakerYourself() {
  const [run, setRun] = useState(_randomRun);
  const [runId, setRunId] = useState(0);          // remounts the modal on each run
  const [phase, setPhase] = useState("idle");     // "idle" | "playing" | "done"
  const [result, setResult] = useState(null);     // { outcome, elapsed_ms }
  const [best, setBest] = useState(() => {
    const v = Number(localStorage.getItem(BEST_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  });

  const handleReport = useCallback((outcome, info = {}) => {
    const elapsed = Number(info.elapsed_ms) || 0;
    setResult({ outcome, elapsed_ms: elapsed });
    // A "best" is the longest survival on a successful escape. (Hook point for
    // the future server-side streaker leaderboard — for now, local only.)
    if (outcome === "escaped" && elapsed > 0) {
      setBest(prev => {
        const next = prev == null ? elapsed : Math.max(prev, elapsed);
        try { localStorage.setItem(BEST_KEY, String(next)); } catch { /* ignore */ }
        return next;
      });
    }
  }, []);

  // The modal calls onClose ~2.4s after the result (its FX hold). Swap to the
  // result panel then — instead of the card-mode auto-dismiss.
  const handleClose = useCallback(() => setPhase("done"), []);

  // Start a fresh run — used by both the initial Play and Play again.
  const startRun = useCallback(() => {
    setRun(_randomRun());
    setRunId(id => id + 1);
    setResult(null);
    setPhase("playing");
  }, []);

  return (
    <MinigameStage designW={STAGE_W} designH={STAGE_H}>
      <div className="relative w-full h-full bg-slate-900 overflow-hidden">
        <MinigameField />
        {phase === "playing" ? (
          <StreakerInteractiveModal
            key={runId}
            embedded
            fieldSize={{ w: STAGE_W, h: STAGE_H }}
            isInitiator
            seed={run.seed}
            spawn_edge={run.spawn_edge}
            exit_edge={run.exit_edge}
            onReportResult={handleReport}
            onClose={handleClose}
          />
        ) : phase === "done" ? (
          <ResultPanel result={result} best={best} onPlayAgain={startRun} />
        ) : (
          <StartPanel best={best} onPlay={startRun} />
        )}
      </div>
    </MinigameStage>
  );
}
